// ---------------------------------------------------------------------------
// Side that invokes csvWorker.ts. In Node (tests run with
// environment:"node") `Worker` doesn't exist: falls back to parsing on the
// same thread, which is exactly what it did before — doesn't change test
// behavior, only avoids blocking the UI in a real browser.
// ---------------------------------------------------------------------------
import { parseDailySeries, type DailySeries } from "./dailySeries.ts";
import type { Language } from "./language.ts";
import type { SeriesScale } from "./seriesScale.ts";
import type { WorkerRequest, WorkerResponse } from "./csvWorkerMessages.ts";

const CSV_ENCODING = "windows-1252";

export function parseDailySeriesAsync(
  bytes: ArrayBuffer,
  language: Language,
  scale?: SeriesScale
): Promise<DailySeries> {
  if (typeof Worker === "undefined") {
    const text = new TextDecoder(CSV_ENCODING).decode(bytes);
    return Promise.resolve(parseDailySeries(text, scale));
  }

  return new Promise<DailySeries>((resolve, reject) => {
    const worker = new Worker(new URL("./csvWorker.ts", import.meta.url), { type: "module" });
    // Cleared on whichever handler fires first — `terminate()` and settling
    // the promise are both already idempotent (harmless if the other
    // handler somehow also fired), but clearing both refs closes off that
    // possibility entirely instead of just relying on it being harmless.
    function done() {
      worker.onmessage = null;
      worker.onerror = null;
      worker.terminate();
    }
    worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      const response = event.data;
      if (response.ok) {
        resolve(response.series);
      } else {
        reject(new Error(response.error));
      }
      done();
    };
    worker.onerror = (event) => {
      reject(new Error(event.message || "Error parsing the CSV in the worker."));
      done();
    };
    const request: WorkerRequest = { bytes, language, scale };
    worker.postMessage(request, [bytes]);
  });
}
